/**
 * Browser speech primitives behind the "Hey Nexys" assistant: wake-phrase matching, recognition
 * engine lookup, turning a markdown answer into something worth hearing, and the two chimes.
 *
 * Everything here runs in the browser on the Web Speech API, so there is no key to provision and
 * no audio to store. The trade is that Chrome streams recognition audio to Google's speech service
 * while listening - which is why wake listening is opt-in per user, off by default, and stops the
 * moment the tab is hidden. Swapping in an on-device wake engine later means replacing
 * `createRecognition` and `matchWakePhrase`; nothing above this file knows how the words arrive.
 */

/** What the user is told to say. Keep in sync with WAKE_PATTERN below. */
export const WAKE_PHRASE_LABEL = 'Hey Nexys'

/**
 * Speech engines have never heard of "Nexys" and will offer nexus, nexis, nexsys, necks, next us…
 * so the name is matched by shape rather than spelling: "nex" plus anything, any of the plausible
 * two-word manglings, and a greeting in front so a stray "nexus" in dictation does not wake it.
 * Bare "next" is excluded - "hey, next" is a thing people say.
 */
const GREETING = '(?:hey|hay|hi|hie|ok|okay|yo)'
const NAME = '(?:nex(?!t\\b)[a-z]*|next\\s+(?:us|is)|necks(?:\\s+(?:us|is))?)'
const WAKE_PATTERN = new RegExp(`\\b${GREETING}[,!.]?\\s+${NAME}\\b`, 'i')

export interface WakeMatch {
  /** Offset of the first character of the wake phrase in the transcript. */
  start: number
  /** Offset just past the wake phrase - where the spoken request begins, if there is one. */
  end: number
}

/**
 * Finds the wake phrase in a transcript. Offsets are into the string as given, so the caller can
 * take everything after it as the start of the request: saying "Hey Nexys, what's the payoff on
 * 1005" in one breath should not need a second turn.
 */
export const matchWakePhrase = (transcript: string): WakeMatch | null => {
  const match = WAKE_PATTERN.exec(transcript)
  if (!match) return null
  return { start: match.index, end: match.index + match[0].length }
}

/** Drops a leading wake phrase from captured speech, so it never reaches the assistant as text. */
export const stripWakePhrase = (text: string) => {
  const match = matchWakePhrase(text)
  if (!match || match.start > 2) return text.trim()
  return text.slice(match.end).replace(/^[\s,.!?]+/, '').trim()
}

/* -------------------------------------------------------------------------- */
/*  Recognition engine                                                        */
/* -------------------------------------------------------------------------- */

/**
 * The Web Speech recognition types are still not in lib.dom, so the slice this file uses is
 * declared here rather than pulling in a dependency for four members.
 */
export interface SpeechRecognitionAlternativeLike {
  transcript: string
  confidence: number
}
export interface SpeechRecognitionResultLike {
  readonly length: number
  isFinal: boolean
  [index: number]: SpeechRecognitionAlternativeLike
}
export interface SpeechRecognitionResultListLike {
  readonly length: number
  [index: number]: SpeechRecognitionResultLike
}
export interface SpeechRecognitionEventLike {
  resultIndex: number
  results: SpeechRecognitionResultListLike
}
export interface SpeechRecognitionErrorEventLike {
  error: string
  message?: string
}
export interface SpeechRecognitionLike {
  continuous: boolean
  interimResults: boolean
  lang: string
  maxAlternatives: number
  start(): void
  stop(): void
  abort(): void
  onresult: ((event: SpeechRecognitionEventLike) => void) | null
  onerror: ((event: SpeechRecognitionErrorEventLike) => void) | null
  onend: (() => void) | null
  onstart: (() => void) | null
}

type SpeechRecognitionConstructor = new () => SpeechRecognitionLike

const getRecognitionConstructor = (): SpeechRecognitionConstructor | null => {
  if (typeof window === 'undefined') return null
  const scope = window as any
  return scope.SpeechRecognition || scope.webkitSpeechRecognition || null
}

/** Chrome and Edge, plus Safari behind the webkit prefix. Firefox has no implementation. */
export const isVoiceInputSupported = () => !!getRecognitionConstructor()

export const isVoiceOutputSupported = () =>
  typeof window !== 'undefined' && typeof window.speechSynthesis !== 'undefined'

export const createRecognition = (lang = 'en-US'): SpeechRecognitionLike | null => {
  const Recognition = getRecognitionConstructor()
  if (!Recognition) return null
  const recognition = new Recognition()
  recognition.continuous = true
  // Interim results are what make the wake feel instant: waiting for the final transcript of
  // "Hey Nexys" adds most of a second before anything on screen reacts.
  recognition.interimResults = true
  recognition.lang = lang
  recognition.maxAlternatives = 1
  return recognition
}

/**
 * Asks for the microphone through getUserMedia rather than letting recognition.start() do it.
 * Recognition surfaces a refusal as a generic `not-allowed` error several seconds later with no
 * way to tell "blocked" from "dismissed"; this way the prompt is tied to the click that asked for
 * it and the answer comes back immediately. The track is released straight away - recognition
 * opens its own.
 */
export const requestMicrophoneAccess = async (): Promise<boolean> => {
  if (!navigator.mediaDevices?.getUserMedia) return false
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    stream.getTracks().forEach((track) => track.stop())
    return true
  } catch {
    return false
  }
}

/* -------------------------------------------------------------------------- */
/*  Answer -> speech                                                          */
/* -------------------------------------------------------------------------- */

/** Past this, a spoken answer stops being an answer and becomes a monologue. */
const MAX_SPOKEN_CHARS = 700

/**
 * Turns an assistant answer into something worth listening to. The assistant is asked to answer
 * briefly when the turn came in by voice, but it still returns markdown - and read aloud verbatim,
 * a table is a minute of pipe characters. Structure that only works on screen is dropped and
 * pointed at instead.
 */
export const toSpokenText = (markdown: string): string => {
  let text = markdown || ''
  let droppedStructure = false

  const drop = (pattern: RegExp, replacement = ' ') => {
    if (pattern.test(text)) droppedStructure = true
    text = text.replace(pattern, replacement)
  }

  drop(/```[\s\S]*?```/g)
  drop(/^\s*\|.*\|\s*$(\n^\s*\|.*\|\s*$)*/gm)
  text = text
    .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/`([^`]*)`/g, '$1')
    .replace(/^#{1,6}\s*/gm, '')
    .replace(/^\s*>\s?/gm, '')
    .replace(/^\s*[-*+]\s+/gm, '')
    .replace(/^\s*([-*_]\s*){3,}$/gm, '')
    .replace(/(\*\*|__)(.*?)\1/g, '$2')
    // No lookbehind here on purpose: a regex literal using it is a parse-time SyntaxError on
    // Safari below 16.4, which would take the whole bundle down rather than just this feature.
    .replace(/(^|[\s(])[*_](\S(?:.*?\S)?)[*_](?=$|[\s).,!?;:])/gm, '$1$2')
    // Line breaks are pauses, not silence; without this the whole answer runs together.
    .replace(/\n{2,}/g, '. ')
    .replace(/\n/g, ', ')
    .replace(/\s{2,}/g, ' ')
    // A removed table or code block leaves its blank lines behind, and those became punctuation a
    // step ago: "the payoff is $412,350.22 as of March 3., , See the statement". Spoken, that is
    // two dead pauses in the middle of the answer. Digits are safe here - the run has to be
    // punctuation on both sides, so "412,350.22" is untouched.
    .replace(/ ([.,;:])/g, '$1')
    .replace(/([.,;:])(\s*[.,;:])+/g, '$1')
    .replace(/(?:\.\s*){2,}/g, '. ')
    .replace(/([.,;:])(?=[A-Za-z])/g, '$1 ')
    .trim()

  if (text.length > MAX_SPOKEN_CHARS) {
    const head = text.slice(0, MAX_SPOKEN_CHARS)
    const lastStop = Math.max(head.lastIndexOf('. '), head.lastIndexOf('? '), head.lastIndexOf('! '))
    text = (lastStop > MAX_SPOKEN_CHARS / 3 ? head.slice(0, lastStop + 1) : head).trim()
    droppedStructure = true
  }

  if (droppedStructure) text += ' The rest is on screen.'
  return text.trim()
}

/**
 * Chrome silently truncates long utterances and stops firing `onend` on them, which strands the
 * whole state machine in "speaking". Sentence-sized chunks keep every utterance short enough to
 * survive, and give barge-in a seam to cut at.
 */
const MAX_CHUNK_CHARS = 180

export const chunkForSpeech = (text: string): string[] => {
  const sentences = text.match(/[^.!?]+[.!?]*\s*/g) || (text ? [text] : [])
  const chunks: string[] = []
  let current = ''
  sentences.forEach((sentence) => {
    if (current && current.length + sentence.length > MAX_CHUNK_CHARS) {
      chunks.push(current.trim())
      current = ''
    }
    // A single sentence longer than a chunk is spoken whole rather than cut mid-clause.
    current += sentence
  })
  if (current.trim()) chunks.push(current.trim())
  return chunks
}

/** Prefers a natural en-US voice, then any en voice, then whatever the platform offers. */
export const pickVoice = (voices: SpeechSynthesisVoice[], preferredUri?: string) => {
  if (!voices.length) return null
  if (preferredUri) {
    const chosen = voices.find((voice) => voice.voiceURI === preferredUri)
    if (chosen) return chosen
  }
  const preferredNames = ['Google US English', 'Samantha', 'Microsoft Aria', 'Microsoft Jenny', 'Alex']
  for (const name of preferredNames) {
    const match = voices.find((voice) => voice.name.includes(name))
    if (match) return match
  }
  return voices.find((voice) => voice.lang === 'en-US') || voices.find((voice) => voice.lang.startsWith('en')) || voices[0]
}

/* -------------------------------------------------------------------------- */
/*  Chimes                                                                    */
/* -------------------------------------------------------------------------- */

let audioContext: AudioContext | null = null

const getAudioContext = () => {
  if (typeof window === 'undefined') return null
  const Ctor = (window as any).AudioContext || (window as any).webkitAudioContext
  if (!Ctor) return null
  if (!audioContext) audioContext = new Ctor()
  // Autoplay policy suspends the context when it is created outside a gesture.
  if (audioContext && audioContext.state === 'suspended') audioContext.resume().catch(() => {})
  return audioContext
}

/**
 * Two blips, rising to acknowledge the wake and falling to say it stopped listening. Synthesised
 * rather than shipped as audio files: it is a few oscillator nodes, and it keeps the sound out of
 * the build and off the CDN.
 */
export const playChime = (kind: 'wake' | 'done') => {
  const context = getAudioContext()
  if (!context) return
  const tones = kind === 'wake' ? [660, 880] : [660, 440]
  tones.forEach((frequency, index) => {
    const startAt = context.currentTime + index * 0.09
    const oscillator = context.createOscillator()
    const gain = context.createGain()
    oscillator.type = 'sine'
    oscillator.frequency.value = frequency
    gain.gain.setValueAtTime(0.0001, startAt)
    gain.gain.exponentialRampToValueAtTime(0.08, startAt + 0.02)
    gain.gain.exponentialRampToValueAtTime(0.0001, startAt + 0.12)
    oscillator.connect(gain)
    gain.connect(context.destination)
    oscillator.start(startAt)
    oscillator.stop(startAt + 0.14)
  })
}
